import { openingHours } from '@/constants/garage'
import { formatClock } from '@/lib/format'

export type OpeningTone = 'open' | 'soon' | 'closed'

export type OpeningStatus = {
    isOpen: boolean
    tone: OpeningTone
    state: string
    short: string
    detail: string
    compact: string
    today: number
}

const WEEKDAYS: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6
}

// Le garage vit à l'heure de Paris, quel que soit le fuseau du visiteur.
export const getParisClock = (date: Date): { day: number, minutes: number } => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Paris',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(date)

    const read = (type: string): string => parts.find(part => part.type === type)?.value ?? ''

    return {
        day: WEEKDAYS[read('weekday')] ?? date.getDay(),
        minutes: Number(read('hour')) * 60 + Number(read('minute'))
    }
}

const getDay = (day: number) => openingHours.find(item => item.day === day)

export const getOpeningStatus = (date: Date): OpeningStatus => {
    const { day, minutes } = getParisClock(date)
    const today = getDay(day)

    if (today?.open != null && today.close != null && minutes >= today.open && minutes < today.close) {
        const closingSoon = today.close - minutes <= 30

        return {
            isOpen: true,
            tone: closingSoon ? 'soon' : 'open',
            state: closingSoon ? 'Ferme bientôt' : 'Ouvert',
            short: 'Ouvert',
            detail: closingSoon ? `à ${formatClock(today.close)}` : `ferme à ${formatClock(today.close)}`,
            compact: `jusqu’à ${formatClock(today.close)}`,
            today: day
        }
    }

    if (today?.open != null && minutes < today.open) {
        return {
            isOpen: false,
            tone: 'closed',
            state: 'Fermé',
            short: 'Fermé',
            detail: `ouvre à ${formatClock(today.open)}`,
            compact: `dès ${formatClock(today.open)}`,
            today: day
        }
    }

    for (let offset = 1; offset <= 7; offset += 1) {
        const next = getDay((day + offset) % 7)

        if (next?.open == null) continue

        const when = offset === 1 ? 'demain' : next.label.toLowerCase()

        return {
            isOpen: false,
            tone: 'closed',
            state: 'Fermé',
            short: 'Fermé',
            detail: `ouvre ${when} à ${formatClock(next.open)}`,
            compact: `${when} ${formatClock(next.open)}`,
            today: day
        }
    }

    return { isOpen: false, tone: 'closed', state: 'Fermé', short: 'Fermé', detail: '', compact: '', today: day }
}
